<template>
  <b-modal
    ref="modal"
    :title="modalTitle"
    size="xl"
    modal-class="pdf-viewer-modal"
    body-class="p-0"
    hide-footer
    scrollable
    @hidden="handleHidden"
    hide-header
  >
    <div class="pdf-viewer-modal__shell">
      <div class="pdf-viewer-modal__toolbar border-bottom px-4 py-3">
        <PdfViewerToolbar
          :title="modalTitle"
          :current-page="currentPage"
          :total-pages="totalPages"
          :zoom-level="zoomLevel"
          :can-download="canDownload"
          :can-print="canPrint"
          :disabled="isBusy"
          @prev-page="setPage(currentPage - 1)"
          @next-page="setPage(currentPage + 1)"
          @jump-page="setPage"
          @zoom-in="zoomIn"
          @zoom-out="zoomOut"
          @fit-width="fitWidth"
          @rotate="rotateClockwise"
          @download="downloadCurrent"
          @print="printCurrent"
          @close="close"
        />
      </div>

      <div class="pdf-viewer-modal__body">
        <PdfViewerStateBanner
          v-if="status === 'error'"
          status="error"
          :title="error.title"
          :message="error.message"
          @retry="reload"
          @close="close"
        />

        <PdfViewerStateBanner
          v-else-if="status === 'cooldown'"
          status="loading"
          title="PDF đang được chuẩn bị"
          :message="cooldownMessage"
        />

        <div v-else-if="resolvedSourceUrl" class="pdf-viewer-modal__viewport-shell">
          <b-alert v-if="noticeErrorMessage" show variant="warning" class="m-3 mb-0">
            {{ noticeErrorMessage }}
          </b-alert>

          <b-overlay :show="status === 'loading'" opacity="0.85" rounded="sm" blur="1px">
            <PdfViewerViewport
              ref="viewport"
              :src="resolvedSourceUrl"
              :page="currentPage"
              :zoom-level="zoomLevel"
              :rotation="rotation"
              :fit-mode="fitMode"
              @document-loaded="handleDocumentLoaded"
              @page-rendered="handlePageRendered"
              @render-error="handleRenderError"
              @rendering-state="handleRenderingState"
            />
          </b-overlay>
        </div>

        <PdfViewerStateBanner
          v-else
          status="loading"
          title="Đang tải tài liệu"
          message="Đang lấy dữ liệu PDF và khởi tạo render."
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { getToastInstance } from '@/utils/toastHelper'
import { downloadPdfUrl, normalizePdfSource, printPdfUrl } from './utils/pdfSource.utils'
import PdfViewerStateBanner from './PdfViewerStateBanner.vue'
import PdfViewerToolbar from './PdfViewerToolbar.vue'
import PdfViewerViewport from './PdfViewerViewport.vue'

const { makeToastFailure } = getToastInstance()

function getDefaultError() {
  return {
    title: '',
    message: '',
  }
}

function getDefaultOpenConfig() {
  return {
    title: '',
    loadPdf: null,
    downloadName: 'document.pdf',
    canDownload: false,
    canPrint: false,
    eventContext: {},
    onEvent: null,
  }
}

function normalizeRetryAfterSeconds(value, fallback = 5) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback
  }

  return Math.ceil(parsed)
}

export default {
  name: 'PdfViewerModal',
  components: {
    PdfViewerToolbar,
    PdfViewerViewport,
    PdfViewerStateBanner,
  },
  data() {
    return {
      status: 'idle',
      currentPage: 1,
      totalPages: 0,
      zoomLevel: 1.2,
      fitMode: 'custom',
      rotation: 0,
      resolvedSourceUrl: '',
      revokeSource: null,
      error: getDefaultError(),
      openConfig: getDefaultOpenConfig(),
      isVisible: false,
      noticeErrorMessage: '',
      cooldownRemainingSeconds: 0,
      retryTimer: null,
    }
  },
  computed: {
    modalTitle() {
      return this.openConfig.title || 'PDF Viewer'
    },
    canDownload() {
      return this.openConfig.canDownload
    },
    canPrint() {
      return this.openConfig.canPrint
    },
    isBusy() {
      return this.status === 'loading' || this.status === 'rendering' || this.status === 'cooldown'
    },
    cooldownMessage() {
      const seconds = Math.max(1, Number(this.cooldownRemainingSeconds) || 0)
      return `Hệ thống đang sinh PDF. Tự động thử lại sau ${seconds} giây.`
    },
  },
  methods: {
    async open(config = {}) {
      this.resetState()
      this.openConfig = {
        ...getDefaultOpenConfig(),
        ...config,
      }

      this.isVisible = true
      this.$refs.modal.show()
      this.emitViewerEvent('opened', {
        currentPage: this.currentPage,
      })

      await this.loadSource()
    },

    close() {
      this.$refs.modal.hide()
    },

    async reload() {
      this.clearRetryTimer()
      await this.loadSource()
    },

    async loadSource() {
      if (typeof this.openConfig.loadPdf !== 'function') {
        this.status = 'error'
        this.error = {
          title: 'Thiếu cấu hình viewer',
          message: 'Không có hàm loadPdf để tải tài liệu.',
        }
        return
      }

      this.clearRetryTimer()
      this.clearResolvedSource()
      this.status = 'loading'
      this.error = getDefaultError()
      this.noticeErrorMessage = ''
      this.cooldownRemainingSeconds = 0

      try {
        const payload = await this.openConfig.loadPdf()
        const { viewUrl, revoke } = normalizePdfSource(payload)

        this.resolvedSourceUrl = viewUrl
        this.revokeSource = revoke
        this.currentPage = 1
        this.totalPages = 0
        this.fitMode = 'custom'
        this.zoomLevel = 1.2
        this.rotation = 0
      } catch (error) {
        const isPdfNotReady = error?.code === 'EINVOICE_PDF_NOT_READY'
        const isRetryable = isPdfNotReady && error?.isRetryable !== false

        if (isRetryable) {
          const retryAfterSeconds = normalizeRetryAfterSeconds(error?.retryAfterSeconds, 5)
          this.startCooldownRetry(retryAfterSeconds)
          this.emitViewerEvent('retry-scheduled', {
            code: 'EINVOICE_PDF_NOT_READY',
            retryAfterSeconds,
          })
          return
        }

        this.status = 'error'
        this.error = {
          title: 'Không thể tải PDF',
          message: error?.message || 'Không lấy được tài liệu PDF.',
        }
        this.emitViewerEvent('error', {
          code: 'PDF_SOURCE_LOAD_FAILED',
          message: this.error.message,
        })
      }
    },

    handleDocumentLoaded({ totalPages }) {
      this.totalPages = totalPages
      this.status = 'ready'
      this.noticeErrorMessage = ''
      this.emitViewerEvent('loaded', {
        totalPages,
      })
    },

    startCooldownRetry(retryAfterSeconds) {
      this.clearRetryTimer()

      this.status = 'cooldown'
      this.cooldownRemainingSeconds = normalizeRetryAfterSeconds(retryAfterSeconds, 5)

      const tick = async () => {
        if (!this.isVisible) {
          this.clearRetryTimer()
          return
        }

        if (this.cooldownRemainingSeconds <= 1) {
          this.cooldownRemainingSeconds = 0
          this.clearRetryTimer()
          await this.loadSource()
          return
        }

        this.cooldownRemainingSeconds -= 1
        this.retryTimer = setTimeout(tick, 1000)
      }

      this.retryTimer = setTimeout(tick, 1000)
    },

    clearRetryTimer() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
      }

      this.retryTimer = null
    },

    handlePageRendered({ page, scale }) {
      this.status = 'ready'
      this.zoomLevel = scale
      this.emitViewerEvent('page-change', {
        page,
        zoomLevel: scale,
      })
    },

    handleRenderError(error) {
      this.status = 'error'
      this.error = {
        title: 'Hiển thị PDF thất bại',
        message: error?.message || 'Không render được tài liệu.',
      }
      this.emitViewerEvent('error', error || {
        code: 'PDF_RENDER_FAILED',
        message: this.error.message,
      })
    },

    handleRenderingState({ rendering }) {
      if (this.status === 'loading') return
      this.status = rendering ? 'rendering' : 'ready'
    },

    setPage(page) {
      const nextPage = Math.max(1, Math.min(Number(page) || 1, this.totalPages || 1))
      this.currentPage = nextPage
    },

    zoomIn() {
      this.fitMode = 'custom'
      this.zoomLevel = Math.min(Number((this.zoomLevel + 0.1).toFixed(2)), 3)
      this.emitViewerEvent('zoom-change', {
        zoomLevel: this.zoomLevel,
      })
    },

    zoomOut() {
      this.fitMode = 'custom'
      this.zoomLevel = Math.max(Number((this.zoomLevel - 0.1).toFixed(2)), 0.5)
      this.emitViewerEvent('zoom-change', {
        zoomLevel: this.zoomLevel,
      })
    },

    fitWidth() {
      this.fitMode = 'fit-width'
      this.emitViewerEvent('zoom-change', {
        zoomLevel: this.zoomLevel,
        fitMode: this.fitMode,
      })
    },

    rotateClockwise() {
      this.rotation = (this.rotation + 90) % 360
    },

    downloadCurrent() {
      if (!this.canDownload || !this.resolvedSourceUrl) return

      downloadPdfUrl(this.resolvedSourceUrl, this.openConfig.downloadName)
      this.emitViewerEvent('download', {
        downloadName: this.openConfig.downloadName,
      })
    },

    async printCurrent() {
      if (!this.canPrint || !this.resolvedSourceUrl) return

      try {
        await printPdfUrl(this.resolvedSourceUrl)
        this.noticeErrorMessage = ''
        this.emitViewerEvent('print', {
          downloadName: this.openConfig.downloadName,
        })
      } catch (error) {
        this.noticeErrorMessage = error?.message || 'Không thể in PDF'
        makeToastFailure(this.noticeErrorMessage)
        this.emitViewerEvent('error', {
          code: 'PDF_PRINT_FAILED',
          message: this.noticeErrorMessage,
        })
      }
    },

    handleHidden() {
      this.emitViewerEvent('closed', {
        currentPage: this.currentPage,
      })
      this.clearRetryTimer()
      this.clearResolvedSource()
      this.resetState()
      this.openConfig = getDefaultOpenConfig()
      this.isVisible = false
      this.$emit('closed')
    },

    clearResolvedSource() {
      if (typeof this.revokeSource === 'function') {
        this.revokeSource()
      }

      this.revokeSource = null
      this.resolvedSourceUrl = ''
    },

    resetState() {
      this.status = 'idle'
      this.currentPage = 1
      this.totalPages = 0
      this.zoomLevel = 1.2
      this.fitMode = 'custom'
      this.rotation = 0
      this.error = getDefaultError()
      this.noticeErrorMessage = ''
      this.cooldownRemainingSeconds = 0
    },

    emitViewerEvent(name, payload = {}) {
      const fullPayload = {
        ...this.openConfig.eventContext,
        ...payload,
      }

      this.$emit(name, fullPayload)

      if (typeof this.openConfig.onEvent === 'function') {
        this.openConfig.onEvent(name, fullPayload)
      }
    },
  },
}
</script>

<style lang="scss">
.pdf-viewer-modal {
  .modal-dialog {
    max-width: 92vw;
  }
}
</style>

<style lang="scss" scoped>
.pdf-viewer-modal__shell {
  min-height: 72vh;
  display: flex;
  flex-direction: column;
}

.pdf-viewer-modal__body {
  flex: 1;
  min-height: 0;
}
</style>
